import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import {
  Users,
  Award,
  Target,
  MapPin,
  Star,
  Mic,
  Globe,
  Film,
  Trophy,
  Building,
  Lightbulb
} from 'lucide-react';
import Card from '../components/UI/Card';
import ContactPreview from '../components/Home/ContactPreview';

const MilestoneProjects: React.FC = () => {
  const projects: { name: string; icon: any }[] = [
    { name: "Promax Scientific Developers Private Ltd - INNVOLUTION", icon: Mic },
    { name: "Euro PCR 2025", icon: Globe },
    { name: "Meet and Greet Event, Edinburgh, Scotland Tourism Development Corporation", icon: MapPin },
    { name: "Thematic Art Installations - 51st & 52nd International Film Festival of India", icon: Film },
    { name: "Opening and Closing Ceremony - National Under-19 Football Talent Hunt", icon: Trophy },
    { name: "Oorja", icon: Film },
    { name: "Goa State Film Festival - Exclusive Curation of On-Stage Programming", icon: Film },
    { name: "Golden Goa High-Street Entertainment Zone", icon: Star },
    { name: "Entertainment Zone at Art Park - 50th International Film Festival of India", icon: Film },
    { name: "Goa Krishi Mohatsav - Agriculture Sector", icon: Target },
    { name: "On-Stage Programming - Aqua Goa Mega Fish Festival 2020", icon: Target },
    { name: "Safe Soil Campaign", icon: Award },
    { name: "Aqua Goa Mega Fish Festival 2023, 2024 & 2025", icon: Target },
    { name: "Global Maritime India Summit 2023 - BKC Mumbai", icon: Globe },
    { name: "Global Ayurveda Conference - Thiruvananthapuram", icon: Users },
    { name: "India International Trade Fair (IITF) 2024 - Bharat Mandapam, New Delhi", icon: Target },
    { name: "Goa Osh Expo and Summit 2025", icon: Target },
    { name: "Jal Jeevan Mission", icon: Building },
    { name: "Launch of PM Vishwakarma Yojana", icon: Building },
    { name: "Launch of Bhima Sakhi Yojana", icon: Building },
    { name: "Yuva Tourism Club Meet", icon: MapPin },
    { name: "Planning and Executing - Goa Open Innovation Challenge", icon: Lightbulb },
    { name: "Anubhuti - National Conclave on Financial Inclusion", icon: Users }
  ];

  return (
    <>
      <Helmet>
        <title>Milestone Projects - Mirchi Republic Media Production</title>
        <meta name="description" content="Explore our milestone projects including government events, cultural festivals, tourism campaigns, and corporate productions. 15+ years of excellence in event management." />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg viewBox="0 0 800 600" className="w-full h-full" fill="currentColor">
              <defs>
                <pattern id="milestone-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                  <circle cx="20" cy="20" r="2" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="800" height="600" fill="url(#milestone-pattern)" />
            </svg>
          </div>
          
          <div className="container-grid relative z-10">
            <div className="col-span-12 text-center">
              <motion.h1 
                className="text-5xl lg:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Milestone Projects
              </motion.h1>
              <motion.p 
                className="text-xl lg:text-2xl opacity-90 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Showcasing Excellence Through Landmark Events and Productions
              </motion.p>
            </div>
          </div>
        </section>

        {/* Projects List */}
        <section className="py-20 bg-white dark:bg-dark-bg">
          <div className="container-grid">
            <div className="col-span-12">
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-6">Our Projects</h2>
                <p className="text-xl text-neutral-600 dark:text-neutral-400">
                  A comprehensive showcase of events and projects we have successfully managed
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Card className="p-6 h-full hover:shadow-lg transition-shadow duration-300">
                      <project.icon className="w-12 h-12 text-brand-1 mb-4" />
                      <h3 className="text-lg mb-2">{project.name}</h3>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <ContactPreview />
      </div>
    </>
  );
};

export default MilestoneProjects;